export type DateOnlyStringValue = string | null | undefined
export type StoredDateOnlyValue = Date | DateOnlyStringValue

export const DATE_ONLY_DISPLAY_FORMAT = 'dd/MM/yyyy'

const DATE_ONLY_PREFIX =
  /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:[.,]\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3])(?::?[0-5]\d)?)?$)/

const isValidCalendarDate = (year: number, month: number, day: number): boolean => {
  const candidate = new Date(Date.UTC(year, month - 1, day))

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  )
}

export const toDateOnlyString = (value: DateOnlyStringValue): string => {
  if (!value) {
    return ''
  }

  const match = DATE_ONLY_PREFIX.exec(value.trim())
  if (match) {
    const [, year, month, day] = match

    return isValidCalendarDate(Number(year), Number(month), Number(day))
      ? `${year}-${month}-${day}`
      : ''
  }

  return ''
}

/**
 * Converts UTC-anchored store Dates to date-only strings.
 * Picker-emitted local Dates must use localDateToDateOnlyString instead.
 */
export const utcDateToDateOnlyString = (value: Date | null | undefined): string => {
  if (!value || Number.isNaN(value.getTime())) {
    return ''
  }

  return value.toISOString().slice(0, 10)
}

export const storedDateToDateOnlyString = (value: StoredDateOnlyValue): string =>
  value instanceof Date ? utcDateToDateOnlyString(value) : toDateOnlyString(value)

export type StoredDateOnlyResolution = {
  dateOnly: string
  status: 'valid' | 'missing' | 'invalid'
}

export const resolveStoredDateOnlyValue = (
  value: StoredDateOnlyValue
): StoredDateOnlyResolution => {
  if (value === null || value === undefined || value === '') {
    return { dateOnly: '', status: 'missing' }
  }

  const dateOnly = storedDateToDateOnlyString(value)
  return dateOnly
    ? { dateOnly, status: 'valid' }
    : { dateOnly: '', status: 'invalid' }
}

export const isInvalidStoredDateOnlyValue = (value: StoredDateOnlyValue): boolean =>
  resolveStoredDateOnlyValue(value).status === 'invalid'

/**
 * Converts a picker-emitted local Date to its selected calendar day.
 */
export const localDateToDateOnlyString = (value: Date | null): string => {
  if (!value || Number.isNaN(value.getTime())) {
    return ''
  }

  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const dateOnlyStringToLocalDate = (value: DateOnlyStringValue): Date | null => {
  const dateOnly = toDateOnlyString(value)
  if (!dateOnly) {
    return null
  }

  const [year, month, day] = dateOnly.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export const formatDateOnly = (
  value: DateOnlyStringValue,
  emptyValue = ''
): string => {
  const dateOnly = toDateOnlyString(value)
  if (!dateOnly) {
    return emptyValue
  }

  const [year, month, day] = dateOnly.split('-')

  // Product requirement: lifecycle dates use dd/MM/yyyy in both UI languages.
  return `${day}/${month}/${year}`
}
